import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../lib/apiClient";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Search, Package, ExternalLink, RefreshCw, ChevronLeft, Check, Clock } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Reset the body pointer-events lock that Radix can leave behind when a Dialog
// is closed while another Dialog (the parent add/edit form) is still open.
const unlockBody = () => setTimeout(() => { try { document.body.style.pointerEvents = ""; } catch (e) {} }, 0);

export const ProductPickerDialog = ({ open, onOpenChange, onSelect }) => {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [live, setLive] = useState(null);
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
      setQ(""); setSelected(null); setLive(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return products;
    return products.filter((p) =>
      [p.name, p.vendor, p.category, p.lead_time, String(p.price || "")]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(t)));
  }, [products, q]);

  const handleOpenChange = (o) => { onOpenChange(o); if (!o) unlockBody(); };

  const pick = (p) => { setSelected(p); setLive(null); };

  const checkLive = async () => {
    if (!selected?.link) { toast.error("No product link saved for this item"); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/products/smart-fill", { url: selected.link });
      setLive({ price: data.price || 0 });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const applyLivePrice = async () => {
    setUpdating(true);
    try {
      await api.put(`/products/${selected.id}`, { price: live.price });
      setSelected((s) => ({ ...s, price: live.price }));
      setProducts((arr) => arr.map((x) => (x.id === selected.id ? { ...x, price: live.price } : x)));
      toast.success("Saved price updated");
      setLive(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUpdating(false); }
  };

  const use = () => { onSelect(selected); onOpenChange(false); unlockBody(); };

  const priceChanged = live && Number(live.price) > 0 && Number(live.price) !== Number(selected?.price || 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl z-[70]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622]">
            {selected ? "Confirm pricing" : "Choose from Product Library"}
          </DialogTitle>
          <DialogDescription className="sr-only">Select a saved product and confirm its current pricing</DialogDescription>
        </DialogHeader>

        {!selected ? (
          <>
            <div className="relative mt-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A68A6B]" />
              <Input data-testid="product-picker-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by brand, category, price or lead time…"
                className="pl-9 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <div className="mt-3 max-h-[55vh] overflow-y-auto -mx-1 px-1">
              {filtered.length === 0 ? (
                <div className="border border-dashed border-[#D9CBBF] p-10 text-center text-[#9b938b] flex flex-col items-center gap-2">
                  <Package size={26} strokeWidth={1} />
                  <span className="text-sm">{products.length === 0 ? "Your Product Library is empty." : "No matching products."}</span>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {filtered.map((p) => (
                    <button key={p.id} type="button" data-testid={`product-pick-${p.id}`} onClick={() => pick(p)}
                      className="text-left border border-[#EBE5E0] bg-white hover:border-[#A68A6B] transition-colors flex">
                      <div className="w-20 h-20 shrink-0 bg-[#F2EFEB] overflow-hidden">
                        {p.image_url
                          ? <img src={fileSrc(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-[#C9BBAE]"><Package size={20} /></div>}
                      </div>
                      <div className="p-3 min-w-0 flex-1">
                        <p className="text-sm text-[#2B2622] font-medium truncate">{p.name}</p>
                        {p.vendor && <p className="text-[11px] text-[#9b938b] truncate">{p.vendor}</p>}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {p.price > 0 && <span className="text-xs text-[#8C7154]">{fmt(p.price)}</span>}
                          {p.lead_time && <span className="text-[10px] text-[#9b938b] inline-flex items-center gap-0.5"><Clock size={10} />{p.lead_time}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-2 space-y-4" data-testid="confirm-pricing-panel">
            <div className="flex gap-4 border border-[#EBE5E0] p-3">
              <div className="w-24 h-24 shrink-0 bg-[#F2EFEB] overflow-hidden">
                {selected.image_url
                  ? <img src={fileSrc(selected.image_url)} alt={selected.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[#C9BBAE]"><Package size={22} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#2B2622] font-medium">{selected.name}</p>
                {selected.vendor && <p className="text-[12px] text-[#5C5651]">{selected.vendor}</p>}
                {selected.vendor_contact && <p className="text-[11px] text-[#9b938b]">{selected.vendor_contact}</p>}
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="text-sm text-[#8C7154]" data-testid="confirm-saved-price">Saved: {fmt(selected.price)}</span>
                  {selected.lead_time && <span className="text-[11px] text-[#9b938b] inline-flex items-center gap-1"><Clock size={11} />{selected.lead_time}</span>}
                </div>
              </div>
            </div>

            <p className="text-[12px] text-[#5C5651]">
              Prices can change at the vendor. Open the item to verify, then refresh to the current price if it changed.
            </p>

            <div className="flex flex-wrap gap-2">
              <a href={selected.link || undefined} target="_blank" rel="noopener noreferrer"
                data-testid="confirm-open-web"
                className={`inline-flex items-center gap-2 border px-3 py-2 text-xs uppercase tracking-wider ${selected.link ? "border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB]" : "border-[#EBE5E0] text-[#C9BBAE] pointer-events-none"}`}>
                <ExternalLink size={14} /> Open in web
              </a>
              <Button type="button" data-testid="confirm-check-price" onClick={checkLive} disabled={checking || !selected.link}
                variant="outline" className="rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs">
                <RefreshCw size={14} className={`mr-1.5 ${checking ? "animate-spin" : ""}`} /> {checking ? "Checking…" : "Check live price"}
              </Button>
            </div>

            {live && (
              <div data-testid="live-price-result" className={`border p-3 text-sm ${priceChanged ? "border-[#C49A4A] bg-[#FBF6EA]" : "border-[#506B52] bg-[#F1F5F1]"}`}>
                {priceChanged ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-[#8C7154]">Live price <strong>{fmt(live.price)}</strong> differs from saved {fmt(selected.price)}.</span>
                    <Button type="button" data-testid="confirm-update-price" onClick={applyLivePrice} disabled={updating}
                      className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs">
                      Update to {fmt(live.price)}
                    </Button>
                  </div>
                ) : (
                  <span className="text-[#506B52] inline-flex items-center gap-1.5"><Check size={15} /> {live.price > 0 ? "Pricing is up to date." : "Couldn't read a live price — verify on the vendor page."}</span>
                )}
              </div>
            )}

            <div className="flex items-center justify-between border-t border-[#EBE5E0] pt-4">
              <button type="button" data-testid="confirm-back" onClick={() => { setSelected(null); setLive(null); }}
                className="inline-flex items-center gap-1 text-sm text-[#5C5651] hover:text-[#A68A6B]">
                <ChevronLeft size={16} /> Back
              </button>
              <Button type="button" data-testid="confirm-use-product" onClick={use}
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                Use this product
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
